import _ from '@sparta.global.vendors/lodash';
import Module, { registerModuleClass } from '@sparta.global.utilities/sparta-module-utility/dist/es6.js';
import benchmarkUtility from '@sparta.sales.utilities/benchmark-utility/dist/es6.js';
import Handlebars from '@sparta.global.vendors/handlebars';
import * as DomUtility from '@sparta.global.utilities/dom-utility/dist/es6.js';
import remoteConsole from '@sparta.homepage.utilities/remote-logging-utility/dist/es6.js';
import animationUtility from '@sparta.homepage.utilities/animation-utility/dist/es6.js';
import modalUtility from '@sparta.homepage.utilities/modal-utility/dist/es6.js';
import picturefill from '@sparta.global.vendors/picturefill';
import spartaMediaQueryUtility from '@sparta.global.utilities/sparta-mediaquery-utility/dist/es6.js';
import { json } from '@sparta.global.helpers/json-helper/dist/es6.js';
import { layerContent, link } from '@sparta.global.helpers/sparta-helper/dist/es6.js';
import { picture } from '@sparta.global.helpers/picture-helper/dist/es6.js';
import { isArray, curve } from '@sparta.homepage.helpers/homepage-helper/dist/es6.js';
import { compare } from '@sparta.global.helpers/comparisons-helper/dist/es6.js';

/**
 * VideoBackground class
 * @class VideoBackground
 * @abstract
 */
class VideoBackground {
  /**
   * Getter for current viewport.
   * @readonly
   * @returns {String} viewport
   */
  get currentViewport() {
    return this.module.Store.getData('currentMQ').viewport;
  }

  /**
   * Constructor
   * @param {highlightLowlightLegacyModuleJS} module module
   * @param {HTMLElement} highlightElem elem to highlight
   * @param {Object} videoData data
   */
  constructor(module, highlightElem, videoData) {
    this.module = module;
    this.highlightElem = highlightElem;
    this.videoData = videoData;
    this.videoEl = highlightElem.querySelector('.highlight-video-background__video');
    this.webmSource = this.videoEl.querySelector('.webm-source');
    this.mp4Source = this.videoEl.querySelector('.mp4-source');
    const {
      target
    } = module.settings;
    const highlights = [...target.querySelectorAll('.highlight')];
    const highlightIndex = highlights.indexOf(highlightElem);
    const animationControlsTemplate = target.querySelector('.animation-controls-template');
    const animationControls = animationControlsTemplate.cloneNode(true);
    animationControls.classList.remove('animation-controls-template');
    animationControls.classList.add('animation-controls');
    this.highlightElem.appendChild(animationControls);
    this.pauseButton = animationControls.querySelector('.animation-btn-pause');
    this.pauseButton.setAttribute('id', `${this.pauseButton.id}${highlightIndex + 1}`);
    this.playButton = animationControls.querySelector('.animation-btn-play');
    this.playButton.setAttribute('id', `${this.playButton.id}${highlightIndex + 1}`);
    this.animationControls = animationControls;
  }

  /**
   * Initializes instance.
   * @returns {void}
   */
  init() {
    let scrolling = false;
    this.bindResizeEvents();
    this.bindPlayPauseEvents();
    switch (this.videoData.playTrigger) {
      case 'scroll':
        // play video when element is scrolled entirely into view
        window.addEventListener('scroll', () => {
          if (!scrolling) {
            window.requestAnimationFrame(() => {
              if (this.isScrolledIntoView()) {
                this.videoEl.play();
              } else {
                this.pauseAndResetVideo();
              }
              scrolling = false;
            });
            scrolling = true;
          }
        });
        break;
      case 'hover':
        // play video on mouseover, pause/reset on leave
        this.highlightElem.addEventListener('mouseenter', () => {
          this.videoEl.play(this.videoEl);
        });
        this.highlightElem.addEventListener('mouseleave', () => {
          this.pauseAndResetVideo();
        });
        break;
      case 'focus':
        // play video on focus, pause/reset on focusout
        this.highlightElem.addEventListener('focus', () => {
          this.videoEl.play(this.videoEl);
        });
        this.highlightElem.addEventListener('focusout', () => {
          this.pauseAndResetVideo();
        });
        break;
    }
  }

  /**
   * Bind play / pause events.
   * @returns {void}
   */
  bindPlayPauseEvents() {
    this.videoEl.addEventListener('play', () => {
      if (this.hasVideoSource()) {
        this.showPauseButton();
      } else {
        this.hideAllButtons();
      }
    });
    this.videoEl.addEventListener('pause', () => {
      if (this.hasVideoSource()) {
        this.showPlayButton();
      } else {
        this.hideAllButtons();
      }
    });
    this.pauseButton.addEventListener('click', () => {
      this.videoEl.pause();

      // In click handler to prevent focus on pageload.
      setTimeout(this.playButton.focus.bind(this.playButton), 200);
    });
    this.playButton.addEventListener('click', () => {
      this.videoEl.play();

      // In click handler to prevent focus on pageload.
      setTimeout(this.pauseButton.focus.bind(this.pauseButton), 200);
    });
  }

  /**
   * Checks if videoEl is scrolled in view.
   * @returns {Boolean} is scrolled
   */
  isScrolledIntoView() {
    const rect = this.videoEl.getBoundingClientRect();
    return rect.top >= 0 && rect.bottom <= window.innerHeight;
  }

  /**
   * Bind resize events.
   * @returns {void}
   */
  bindResizeEvents() {
    if (!this.videoData.videoInterchange) {
      if (this.videoData.default) {
        this.videoData.default.forEach(sourceType => {
          if (sourceType.type === 'video/webm') {
            this.webmSource.src = sourceType.src;
          }
          if (sourceType.type === 'video/mp4') {
            this.mp4Source.src = sourceType.src;
          }
        });
        this.loadVideo();
      }
    } else {
      this.selectSourceForViewport(this.currentViewport);
      this.module.Events.sub(spartaMediaQueryUtility.EVENTS.BREAKPOINT_CHANGE, ({
        data
      }) => {
        this.selectSourceForViewport(data.viewport);
      });
    }
  }

  /**
   * Selects source for viewport.
   * @param {String} viewport view
   * @returns {void}
   */
  selectSourceForViewport(viewport) {
    switch (viewport) {
      case 'large':
      case 'largeCentered':
      case 'xlarge':
      case 'xxlarge':
        this.loadSrc('large');
        break;
      case 'medium':
        this.loadSrc('medium');
        break;
      case 'small':
      default:
        this.loadSrc('small');
    }
  }

  /**
   * Loads video source.
   * @param {String} viewport view
   * @returns {void}
   */
  loadSrc(viewport) {
    const interchange = this.videoData.videoInterchange;
    if (interchange[viewport]) {
      interchange[viewport].forEach(source => {
        if (source.type === 'video/webm') {
          this.webmSource.src = source.src;
        }
        if (source.type === 'video/mp4') {
          this.mp4Source.src = source.src;
        }
      });
    } else {
      this.webmSource.removeAttribute('src');
      this.mp4Source.removeAttribute('src');
    }
    this.loadVideo();
  }

  /**
   * Has video source.
   * @returns {Boolean} has video source
   */
  hasVideoSource() {
    return this.webmSource.src || this.mp4Source.src;
  }

  /**
   * Loads video.
   * @returns {void}
   */
  loadVideo() {
    this.videoEl.oncanplaythrough = () => {
      this.videoEl.classList.remove('hide');
      if (this.videoPlaceholder) {
        this.videoPlaceholder.classList.add('hide');
      }
    };
    this.videoEl.classList.add('hide');
    if (this.videoPlaceholder) {
      this.videoPlaceholder.classList.remove('hide');
    }
    this.videoEl.load();
    if (this.videoEl.paused && this.hasVideoSource()) {
      this.showPlayButton();
    } else if (!this.hasVideoSource()) {
      this.hideAllButtons();
    }
  }

  /**
   * Pause and resets video.
   * @returns {void}
   */
  pauseAndResetVideo() {
    this.videoEl.pause();
    this.videoEl.currentTime = 0;
  }

  /**
   * Hides play and pause buttons.
   * @returns {void}
   */
  hideAllButtons() {
    this.pauseButton.classList.add('hide');
    this.playButton.classList.add('hide');
  }

  /**
   * Shows the pause button.
   * @returns {void}
   */
  showPauseButton() {
    this.hideAllButtons();
    this.pauseButton.classList.remove('hide');
  }

  /**
   * Shows the play button.
   * @returns {void}
   */
  showPlayButton() {
    this.hideAllButtons();
    this.playButton.classList.remove('hide');
  }
}

const Template$2 = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <div class=\"bottom-link\">\r\n      "
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"data1") : depth0)) != null ? lookupProperty(stack1,"bottomLink") : stack1),{"name":"with","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":6},"end":{"line":23,"column":86}}})) != null ? stack1 : "")
    + "\r\n    </div>\r\n";
},"2":function(container,depth0,helpers,partials,data) {
    var stack1;

  return "<span class=\"chevron-right\">"
    + ((stack1 = container.lambda(depth0, depth0)) != null ? stack1 : "")
    + "</span>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), alias4=container.hooks.helperMissing, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"curve\">\r\n  <div class=\"highlight-lowlight-carousel-container\">\r\n    <div\r\n      class=\"highlight-carousel\"\r\n      id=\"highlight-lowlight-carousel\"\r\n      tabindex=\"-1\"\r\n      role=\"presentation\"\r\n      aria-label=\""
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"data1") : depth0)) != null ? lookupProperty(stack1,"slideAriaLabel") : stack1), depth0))
    + "\"\r\n    >\r\n      <div class=\"carousel-tile highlight\"></div>\r\n      <div class=\"carousel-tile highlight\"></div>\r\n      <div class=\"carousel-tile lowlight\"></div>\r\n      <div class=\"carousel-tile lowlight\"></div>\r\n      <div class=\"carousel-tile lowlight\"></div>\r\n      <div class=\"carousel-tile lowlight\"></div>\r\n    </div>\r\n  </div>\r\n\r\n  "
    + ((stack1 = (lookupProperty(helpers,"curve")||(depth0 && lookupProperty(depth0,"curve"))||alias4).call(alias3,"bottom","#f5f5f5","show-for-large-up",{"name":"curve","hash":{},"data":data,"loc":{"start":{"line":19,"column":2},"end":{"line":19,"column":52}}})) != null ? stack1 : "")
    + "\r\n\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,((stack1 = (depth0 != null ? lookupProperty(depth0,"data1") : depth0)) != null ? lookupProperty(stack1,"bottomLink") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":2},"end":{"line":25,"column":9}}})) != null ? stack1 : "")
    + "\r\n  "
    + ((stack1 = (lookupProperty(helpers,"curve")||(depth0 && lookupProperty(depth0,"curve"))||alias4).call(alias3,"bottom","#fff","show-for-medium-up",{"name":"curve","hash":{},"data":data,"loc":{"start":{"line":27,"column":2},"end":{"line":27,"column":50}}})) != null ? stack1 : "")
    + "\r\n</div>\r\n\r\n<div class=\"animation-controls-template\">\r\n  "
    + ((stack1 = alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"data1") : depth0)) != null ? lookupProperty(stack1,"animationPauseButton") : stack1), depth0)) != null ? stack1 : "")
    + "\r\n  "
    + ((stack1 = alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"data1") : depth0)) != null ? lookupProperty(stack1,"animationPlayButton") : stack1), depth0)) != null ? stack1 : "")
    + "\r\n</div>\r\n\r\n<div type=\"application/json\" id=\"default-content\" data-jcr=\"["
    + alias2((lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||alias4).call(alias3,(depth0 != null ? lookupProperty(depth0,"superHighlight1") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":36,"column":61},"end":{"line":36,"column":85}}}))
    + ","
    + alias2((lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||alias4).call(alias3,(depth0 != null ? lookupProperty(depth0,"superHighlight2") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":36,"column":86},"end":{"line":36,"column":110}}}))
    + ","
    + alias2((lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||alias4).call(alias3,(depth0 != null ? lookupProperty(depth0,"highlight1") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":36,"column":111},"end":{"line":36,"column":130}}}))
    + ", "
    + alias2((lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||alias4).call(alias3,(depth0 != null ? lookupProperty(depth0,"highlight2") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":36,"column":132},"end":{"line":36,"column":151}}}))
    + ","
    + alias2((lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||alias4).call(alias3,(depth0 != null ? lookupProperty(depth0,"highlight3") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":36,"column":152},"end":{"line":36,"column":171}}}))
    + ", "
    + alias2((lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||alias4).call(alias3,(depth0 != null ? lookupProperty(depth0,"highlight4") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":36,"column":173},"end":{"line":36,"column":192}}}))
    + "]\">\r\n</div>\r\n\r\n";
},"useData":true});
const renderTemplate$2 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$2(data, options);
};

const Template$1 = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, helper, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  .highlight-lowlight-legacy-module #"
    + ((stack1 = container.lambda(((stack1 = ((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"LINKS"))) && lookupProperty(stack1,"cta"))) && lookupProperty(stack1,"ID")), depth0)) != null ? stack1 : "")
    + " "
    + ((stack1 = ((helper = (helper = lookupProperty(helpers,"selector") || (depth0 != null ? lookupProperty(depth0,"selector") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(alias1,{"name":"selector","hash":{},"data":data,"loc":{"start":{"line":2,"column":62},"end":{"line":2,"column":76}}}) : helper))) != null ? stack1 : "")
    + " {\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"properties") : depth0),{"name":"each","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":4},"end":{"line":5,"column":13}}})) != null ? stack1 : "")
    + "  }\r\n";
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      "
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"key") || (data && lookupProperty(data,"key"))) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"key","hash":{},"data":data,"loc":{"start":{"line":4,"column":6},"end":{"line":4,"column":14}}}) : helper)))
    + ": "
    + ((stack1 = container.lambda(depth0, depth0)) != null ? stack1 : "")
    + ";\r\n";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"LINKS"))) && lookupProperty(stack1,"cta"))) && lookupProperty(stack1,"ID")),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":4},"end":{"line":11,"column":61}}})) != null ? stack1 : "")
    + ".icon-container img {\r\n      width: "
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"width") : stack1)) != null ? lookupProperty(stack1,"large") : stack1), depth0))
    + " !important;\r\n    }\r\n";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "#"
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"LINKS"))) && lookupProperty(stack1,"cta"))) && lookupProperty(stack1,"ID")), depth0))
    + " ";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "right: "
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"right") : stack1)) != null ? lookupProperty(stack1,"large") : stack1), depth0))
    + " !important;";
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "top: "
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"top") : stack1)) != null ? lookupProperty(stack1,"large") : stack1), depth0))
    + " !important;";
},"11":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"logo") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"with","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":4},"end":{"line":27,"column":13}}})) != null ? stack1 : "");
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"LINKS"))) && lookupProperty(stack1,"cta"))) && lookupProperty(stack1,"ID")),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":6},"end":{"line":21,"column":63}}})) != null ? stack1 : "")
    + ".shl-logo-overlay div {\r\n        "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"top") : depth0),{"name":"if","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":22,"column":8},"end":{"line":22,"column":50}}})) != null ? stack1 : "")
    + "\r\n        "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"bottom") : depth0),{"name":"if","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":8},"end":{"line":23,"column":59}}})) != null ? stack1 : "")
    + "\r\n        "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"left") : depth0),{"name":"if","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":24,"column":8},"end":{"line":24,"column":53}}})) != null ? stack1 : "")
    + "\r\n        "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"right") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":25,"column":8},"end":{"line":25,"column":56}}})) != null ? stack1 : "")
    + "\r\n      }\r\n";
},"13":function(container,depth0,helpers,partials,data) {
    var helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "top: "
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"top") || (depth0 != null ? lookupProperty(depth0,"top") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"top","hash":{},"data":data,"loc":{"start":{"line":22,"column":24},"end":{"line":22,"column":31}}}) : helper)))
    + " !important;";
},"15":function(container,depth0,helpers,partials,data) {
    var helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "bottom: "
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"bottom") || (depth0 != null ? lookupProperty(depth0,"bottom") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"bottom","hash":{},"data":data,"loc":{"start":{"line":23,"column":30},"end":{"line":23,"column":40}}}) : helper)))
    + " !important;";
},"17":function(container,depth0,helpers,partials,data) {
    var helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "left: "
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"left") || (depth0 != null ? lookupProperty(depth0,"left") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"left","hash":{},"data":data,"loc":{"start":{"line":24,"column":26},"end":{"line":24,"column":34}}}) : helper)))
    + " !important;";
},"19":function(container,depth0,helpers,partials,data) {
    var helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "right: "
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"right") || (depth0 != null ? lookupProperty(depth0,"right") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"right","hash":{},"data":data,"loc":{"start":{"line":25,"column":28},"end":{"line":25,"column":37}}}) : helper)))
    + " !important;";
},"21":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"LINKS"))) && lookupProperty(stack1,"cta"))) && lookupProperty(stack1,"ID")),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":33,"column":4},"end":{"line":33,"column":61}}})) != null ? stack1 : "")
    + ".icon-container img {\r\n      width: "
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"width") : stack1)) != null ? lookupProperty(stack1,"medium") : stack1), depth0))
    + " !important;\r\n    }\r\n";
},"23":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "right: "
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"right") : stack1)) != null ? lookupProperty(stack1,"medium") : stack1), depth0))
    + " !important;";
},"25":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "top: "
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"top") : stack1)) != null ? lookupProperty(stack1,"medium") : stack1), depth0))
    + " !important;";
},"27":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"logo") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1),{"name":"with","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":42,"column":4},"end":{"line":49,"column":13}}})) != null ? stack1 : "");
},"29":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"LINKS"))) && lookupProperty(stack1,"cta"))) && lookupProperty(stack1,"ID")),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":55,"column":4},"end":{"line":55,"column":61}}})) != null ? stack1 : "")
    + ".icon-container img {\r\n      width: "
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"width") : stack1)) != null ? lookupProperty(stack1,"small") : stack1), depth0))
    + " !important;\r\n    }\r\n";
},"31":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "right: "
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"right") : stack1)) != null ? lookupProperty(stack1,"small") : stack1), depth0))
    + " !important;";
},"33":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "top: "
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"top") : stack1)) != null ? lookupProperty(stack1,"small") : stack1), depth0))
    + " !important;";
},"35":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"logo") : depth0)) != null ? lookupProperty(stack1,"small") : stack1),{"name":"with","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":64,"column":4},"end":{"line":71,"column":13}}})) != null ? stack1 : "");
},"37":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  /* DEFAULT BREAKPOINT */\r\n"
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,(depth0 != null ? lookupProperty(depth0,"default") : depth0),{"name":"with","hash":{},"fn":container.program(38, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":78,"column":2},"end":{"line":82,"column":11}}})) != null ? stack1 : "")
    + "  /* END DEFAULT BREAKPOINT */\r\n  /* SMALL BREAKPOINT */\r\n"
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,(depth0 != null ? lookupProperty(depth0,"small") : depth0),{"name":"with","hash":{},"fn":container.program(41, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":85,"column":2},"end":{"line":91,"column":11}}})) != null ? stack1 : "")
    + "  /* END SMALL BREAKPOINT */\r\n  /* MEDIUM BREAKPOINT */\r\n"
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,(depth0 != null ? lookupProperty(depth0,"medium") : depth0),{"name":"with","hash":{},"fn":container.program(43, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":94,"column":2},"end":{"line":100,"column":11}}})) != null ? stack1 : "")
    + "  /* END MEDIUM BREAKPOINT */\r\n  /* LARGE BREAKPOINT */\r\n"
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,(depth0 != null ? lookupProperty(depth0,"large") : depth0),{"name":"with","hash":{},"fn":container.program(45, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":103,"column":2},"end":{"line":109,"column":11}}})) != null ? stack1 : "")
    + "  /* END LARGE BREAKPOINT */\r\n";
},"38":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"each","hash":{},"fn":container.program(39, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":79,"column":4},"end":{"line":81,"column":13}}})) != null ? stack1 : "");
},"39":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"styleRulesPartial"),depth0,{"name":"styleRulesPartial","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"41":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  @media screen and (max-width: 767px) {\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"each","hash":{},"fn":container.program(39, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":87,"column":4},"end":{"line":89,"column":13}}})) != null ? stack1 : "")
    + "  }\r\n";
},"43":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  @media screen and (min-width: 768px) and (max-width: 1024px) {\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"each","hash":{},"fn":container.program(39, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":96,"column":4},"end":{"line":98,"column":13}}})) != null ? stack1 : "")
    + "  }\r\n";
},"45":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  @media screen and (min-width: 1025px) {\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"each","hash":{},"fn":container.program(39, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":105,"column":4},"end":{"line":107,"column":13}}})) != null ? stack1 : "")
    + "  }\r\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\r\n@media screen and (min-width: 1025px) {\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"width") : stack1)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"if","hash":{},"fn":container.program(4, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":2},"end":{"line":14,"column":9}}})) != null ? stack1 : "")
    + "  "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"LINKS"))) && lookupProperty(stack1,"cta"))) && lookupProperty(stack1,"ID")),{"name":"if","hash":{},"fn":container.program(5, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":2},"end":{"line":15,"column":59}}})) != null ? stack1 : "")
    + ".icon-container {\r\n    "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"right") : stack1)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"if","hash":{},"fn":container.program(7, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":4},"end":{"line":16,"column":74}}})) != null ? stack1 : "")
    + "\r\n    "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"top") : stack1)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"if","hash":{},"fn":container.program(9, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":17,"column":4},"end":{"line":17,"column":68}}})) != null ? stack1 : "")
    + "\r\n  }\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"logo") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"if","hash":{},"fn":container.program(11, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":19,"column":2},"end":{"line":28,"column":9}}})) != null ? stack1 : "")
    + "}\r\n\r\n@media screen and (min-width: 768px) and (max-width: 1024px) {\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"width") : stack1)) != null ? lookupProperty(stack1,"medium") : stack1),{"name":"if","hash":{},"fn":container.program(21, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":32,"column":2},"end":{"line":36,"column":9}}})) != null ? stack1 : "")
    + "  "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"LINKS"))) && lookupProperty(stack1,"cta"))) && lookupProperty(stack1,"ID")),{"name":"if","hash":{},"fn":container.program(5, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":37,"column":2},"end":{"line":37,"column":59}}})) != null ? stack1 : "")
    + ".icon-container {\r\n    "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"right") : stack1)) != null ? lookupProperty(stack1,"medium") : stack1),{"name":"if","hash":{},"fn":container.program(23, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":38,"column":4},"end":{"line":38,"column":76}}})) != null ? stack1 : "")
    + "\r\n    "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"top") : stack1)) != null ? lookupProperty(stack1,"medium") : stack1),{"name":"if","hash":{},"fn":container.program(25, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":39,"column":4},"end":{"line":39,"column":70}}})) != null ? stack1 : "")
    + "\r\n  }\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"logo") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1),{"name":"if","hash":{},"fn":container.program(27, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":41,"column":2},"end":{"line":50,"column":9}}})) != null ? stack1 : "")
    + "}\r\n\r\n@media screen and (max-width: 767px) {\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"width") : stack1)) != null ? lookupProperty(stack1,"small") : stack1),{"name":"if","hash":{},"fn":container.program(29, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":54,"column":2},"end":{"line":58,"column":9}}})) != null ? stack1 : "")
    + "  "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"LINKS"))) && lookupProperty(stack1,"cta"))) && lookupProperty(stack1,"ID")),{"name":"if","hash":{},"fn":container.program(5, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":59,"column":2},"end":{"line":59,"column":59}}})) != null ? stack1 : "")
    + ".icon-container {\r\n    "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"right") : stack1)) != null ? lookupProperty(stack1,"small") : stack1),{"name":"if","hash":{},"fn":container.program(31, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":60,"column":4},"end":{"line":60,"column":74}}})) != null ? stack1 : "")
    + "\r\n    "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"top") : stack1)) != null ? lookupProperty(stack1,"small") : stack1),{"name":"if","hash":{},"fn":container.program(33, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":61,"column":4},"end":{"line":61,"column":68}}})) != null ? stack1 : "")
    + "\r\n  }\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"logo") : depth0)) != null ? lookupProperty(stack1,"small") : stack1),{"name":"if","hash":{},"fn":container.program(35, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":63,"column":2},"end":{"line":72,"column":9}}})) != null ? stack1 : "")
    + "}\r\n\r\n/* inline style overrides */\r\n"
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,(depth0 != null ? lookupProperty(depth0,"inlineStyles") : depth0),{"name":"with","hash":{},"fn":container.program(37, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":76,"column":0},"end":{"line":111,"column":9}}})) != null ? stack1 : "");
},"main_d":  function(fn, props, container, depth0, data, blockParams, depths) {

  var decorators = container.decorators, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"args":["styleRulesPartial"],"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":7,"column":11}}}) || fn;
  return fn;
  }

,"useDecorators":true,"usePartial":true,"useData":true,"useDepths":true});
const renderTemplate$1 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$1(data, options);
};

const Template = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a\r\n  class=\"clickable-highlight"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"theme") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":28},"end":{"line":4,"column":68}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1)) != null ? lookupProperty(stack1,"CLASS") : stack1),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":68},"end":{"line":4,"column":128}}})) != null ? stack1 : "")
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(alias1,((stack1 = ((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1)) != null ? lookupProperty(stack1,"actionType") : stack1)) != null ? lookupProperty(stack1,"type") : stack1),"==","layer",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":128},"end":{"line":4,"column":221}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"imageInterchangeHover") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":221},"end":{"line":4,"column":265}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0),{"name":"if","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":265},"end":{"line":4,"column":305}}})) != null ? stack1 : "")
    + "\"\r\n  href=\""
    + alias3(alias2(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1)) != null ? lookupProperty(stack1,"URL") : stack1), depth0))
    + "\"\r\n  id=\""
    + alias3(alias2(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1)) != null ? lookupProperty(stack1,"ID") : stack1), depth0))
    + "\"\r\n  tabindex=\"0\"\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1)) != null ? lookupProperty(stack1,"TARGET") : stack1),{"name":"if","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":2},"end":{"line":10,"column":9}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1)) != null ? lookupProperty(stack1,"actionType") : stack1),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":2},"end":{"line":14,"column":9}}})) != null ? stack1 : "")
    + ">\r\n";
},"2":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"theme") : depth0), depth0));
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1)) != null ? lookupProperty(stack1,"CLASS") : stack1), depth0));
},"6":function(container,depth0,helpers,partials,data) {
    return " spa-ui-layer-link spa-fn";
},"8":function(container,depth0,helpers,partials,data) {
    return " has-gif";
},"10":function(container,depth0,helpers,partials,data) {
    return " has-video";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    target=\""
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1)) != null ? lookupProperty(stack1,"TARGET") : stack1), depth0))
    + "\"\r\n";
},"14":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    rel=\""
    + alias1(container.lambda(((stack1 = ((stack1 = ((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1)) != null ? lookupProperty(stack1,"actionType") : stack1)) != null ? lookupProperty(stack1,"options") : stack1)) != null ? lookupProperty(stack1,"rel") : stack1), depth0))
    + "\"\r\n    data-options=\""
    + alias1((lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1)) != null ? lookupProperty(stack1,"actionType") : stack1)) != null ? lookupProperty(stack1,"options") : stack1),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":13,"column":18},"end":{"line":13,"column":60}}}))
    + "\"\r\n";
},"16":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\""
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"theme") : depth0),{"name":"if","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":17,"column":12},"end":{"line":17,"column":51}}})) != null ? stack1 : "")
    + "\">\r\n";
},"17":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"theme") : depth0), depth0));
},"19":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <div class=\"highlight-video-background__container\">\r\n      <div class=\"highlight-video-background"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0)) != null ? lookupProperty(stack1,"style") : stack1),{"name":"if","hash":{},"fn":container.program(20, data, 0),"inverse":container.program(22, data, 0),"data":data,"loc":{"start":{"line":21,"column":44},"end":{"line":21,"column":135}}})) != null ? stack1 : "")
    + "\">\r\n        <video id=\"backgroundVideoHighlight"
    + alias3(alias2(((stack1 = (depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0)) != null ? lookupProperty(stack1,"id") : stack1), depth0))
    + "\" class=\"highlight-video-background__video "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0)) != null ? lookupProperty(stack1,"playTrigger") : stack1),{"name":"if","hash":{},"fn":container.program(24, data, 0),"inverse":container.program(26, data, 0),"data":data,"loc":{"start":{"line":22,"column":113},"end":{"line":22,"column":209}}})) != null ? stack1 : "")
    + "\" "
    + alias3(alias2(((stack1 = (depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0)) != null ? lookupProperty(stack1,"attributes") : stack1), depth0))
    + ">\r\n          <source class=\"webm-source\" type=\"video/webm\">\r\n          <source class=\"mp4-source\" type=\"video/mp4\">\r\n        </video>\r\n      </div>\r\n    </div>\r\n";
},"20":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0)) != null ? lookupProperty(stack1,"style") : stack1), depth0));
},"22":function(container,depth0,helpers,partials,data) {
    return " full-width";
},"24":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0)) != null ? lookupProperty(stack1,"playTrigger") : stack1), depth0));
},"26":function(container,depth0,helpers,partials,data) {
    return "hover";
},"28":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"if","hash":{},"fn":container.program(29, data, 0),"inverse":container.program(37, data, 0),"data":data,"loc":{"start":{"line":30,"column":4},"end":{"line":64,"column":11}}})) != null ? stack1 : "");
},"29":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <div\r\n        class=\"image-holder "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"centerBackgroundImage") : depth0),{"name":"if","hash":{},"fn":container.program(30, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":32,"column":28},"end":{"line":32,"column":81}}})) != null ? stack1 : "")
    + "\"\r\n        aria-hidden=\"true\"\r\n        data-update-container=\"image-logo-update-container\"\r\n      >\r\n        "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(alias1,(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":36,"column":8},"end":{"line":36,"column":36}}}))
    + "\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"imageInterchangeHover") : depth0),{"name":"if","hash":{},"fn":container.program(32, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":37,"column":8},"end":{"line":49,"column":15}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"logo") : depth0),{"name":"if","hash":{},"fn":container.program(34, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":50,"column":8},"end":{"line":52,"column":15}}})) != null ? stack1 : "")
    + "      </div>\r\n";
},"30":function(container,depth0,helpers,partials,data) {
    return "center-background";
},"32":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "          <div class=\"image-holder-hover\">\r\n            "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchangeHover") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":39,"column":12},"end":{"line":39,"column":45}}}))
    + "\r\n          </div>\r\n          <div class=\"image-play-icon\">\r\n            <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"500\" height=\"500\" viewBox=\"0 0 500 500\">\r\n              <g style=\"isolation:isolate\">\r\n                  <path d=\"M418.016 64.239c-3.923-27.461-23.03-45.746-51.508-48.348 0 0-43.255-3.6-114.583-3.6-71.287 0-116.292 3.6-116.292 3.6-28.478 2.6-47.585 20.887-51.508 48.348a364.312 364.312 0 0 0 0 119.086c3.924 27.441 23.031 45.735 51.508 48.337 0 0 45 3.588 116.292 3.588 71.327 0 114.583-3.578 114.583-3.578 28.479-2.6 47.586-20.906 51.508-48.348a364.236 364.236 0 0 0 0-119.086\" fill=\"#f2f2f2\"/>\r\n                  <path d=\"M305.639 123.777l-109.135 59.121V64.666l109.135 59.111\" style=\"mix-blend-mode:multiply\" fill=\"#dc1431\"/>\r\n              </g>\r\n            </svg>\r\n          </div>\r\n";
},"34":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "          "
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"logo") : depth0),{"name":"with","hash":{},"fn":container.program(35, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":51,"column":10},"end":{"line":51,"column":50}}})) != null ? stack1 : "")
    + "\r\n";
},"35":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"logoPartial"),depth0,{"name":"logoPartial","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"37":function(container,depth0,helpers,partials,data) {
    var stack1, helper, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <div class=\"image "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"carouselImage") : depth0),{"name":"if","hash":{},"fn":container.program(38, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":55,"column":24},"end":{"line":55,"column":69}}})) != null ? stack1 : "")
    + "\">\r\n      <img src=\""
    + ((stack1 = ((helper = (helper = lookupProperty(helpers,"image") || (depth0 != null ? lookupProperty(depth0,"image") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(alias1,{"name":"image","hash":{},"data":data,"loc":{"start":{"line":56,"column":16},"end":{"line":56,"column":27}}}) : helper))) != null ? stack1 : "")
    + "\" loading=\"lazy\" alt=\"\" aria-hidden=\"true\">\r\n      </div>\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"carouselImage") : depth0),{"name":"if","hash":{},"fn":container.program(40, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":58,"column":6},"end":{"line":63,"column":13}}})) != null ? stack1 : "");
},"38":function(container,depth0,helpers,partials,data) {
    return "show-for-large-up";
},"40":function(container,depth0,helpers,partials,data) {
    var stack1, helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "        <div\r\n          class=\"image hide-for-large-up\"\r\n          style=\"background-image: url("
    + ((stack1 = ((helper = (helper = lookupProperty(helpers,"carouselImage") || (depth0 != null ? lookupProperty(depth0,"carouselImage") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"carouselImage","hash":{},"data":data,"loc":{"start":{"line":61,"column":39},"end":{"line":61,"column":58}}}) : helper))) != null ? stack1 : "")
    + ");\"\r\n        ></div>\r\n";
},"42":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <div class=\"icon-container\">\r\n      <img src=\""
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"src") : stack1), depth0))
    + "\" loading=\"lazy\" alt=\"\">\r\n    </div>\r\n";
},"44":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <div tabindex=\"-1\" class=\"link chevron-right\">"
    + ((stack1 = container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1)) != null ? lookupProperty(stack1,"content") : stack1), depth0)) != null ? stack1 : "")
    + "</div>\r\n";
},"46":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"link") : depth0),{"name":"if","hash":{},"fn":container.program(47, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":78,"column":6},"end":{"line":80,"column":13}}})) != null ? stack1 : "");
},"47":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "        <div tabindex=\"-1\" class=\"link chevron-right\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"link") : depth0), depth0)) != null ? stack1 : "")
    + "</div>\r\n";
},"49":function(container,depth0,helpers,partials,data) {
    return "</a>\r\n";
},"51":function(container,depth0,helpers,partials,data) {
    return "</div>\r\n";
},"53":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <span class=\"shl-logo-overlay\">\r\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"large") : depth0),{"name":"unless","hash":{},"fn":container.program(54, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":91,"column":4},"end":{"line":93,"column":15}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"large") : depth0),{"name":"if","hash":{},"fn":container.program(56, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":94,"column":4},"end":{"line":102,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"medium") : depth0),{"name":"if","hash":{},"fn":container.program(61, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":103,"column":4},"end":{"line":111,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"small") : depth0),{"name":"if","hash":{},"fn":container.program(64, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":112,"column":4},"end":{"line":120,"column":11}}})) != null ? stack1 : "")
    + "  </span>\r\n";
},"54":function(container,depth0,helpers,partials,data) {
    var stack1, helper, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.hooks.helperMissing, alias3="function", lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <div class=\""
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"location") || (depth0 != null ? lookupProperty(depth0,"location") : depth0)) != null ? helper : alias2),(typeof helper === alias3 ? helper.call(alias1,{"name":"location","hash":{},"data":data,"loc":{"start":{"line":92,"column":18},"end":{"line":92,"column":30}}}) : helper)))
    + "\">"
    + ((stack1 = ((helper = (helper = lookupProperty(helpers,"inlineContent") || (depth0 != null ? lookupProperty(depth0,"inlineContent") : depth0)) != null ? helper : alias2),(typeof helper === alias3 ? helper.call(alias1,{"name":"inlineContent","hash":{},"data":data,"loc":{"start":{"line":92,"column":32},"end":{"line":92,"column":51}}}) : helper))) != null ? stack1 : "")
    + "</div>\r\n";
},"56":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <div class=\"show-for-large-up "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"large") : depth0)) != null ? lookupProperty(stack1,"location") : stack1), depth0))
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"large") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1),{"name":"if","hash":{},"fn":container.program(57, data, 0),"inverse":container.program(59, data, 0),"data":data,"loc":{"start":{"line":96,"column":8},"end":{"line":100,"column":15}}})) != null ? stack1 : "")
    + "      </div>\r\n";
},"57":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "          "
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"large") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1), depth0)) != null ? stack1 : "")
    + "\r\n";
},"59":function(container,depth0,helpers,partials,data) {
    var stack1, helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "          "
    + ((stack1 = ((helper = (helper = lookupProperty(helpers,"inlineContent") || (depth0 != null ? lookupProperty(depth0,"inlineContent") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"inlineContent","hash":{},"data":data,"loc":{"start":{"line":99,"column":10},"end":{"line":99,"column":29}}}) : helper))) != null ? stack1 : "")
    + "\r\n";
},"61":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <div class=\"show-for-medium-only "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"medium") : depth0)) != null ? lookupProperty(stack1,"location") : stack1), depth0))
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"medium") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1),{"name":"if","hash":{},"fn":container.program(62, data, 0),"inverse":container.program(59, data, 0),"data":data,"loc":{"start":{"line":105,"column":8},"end":{"line":109,"column":15}}})) != null ? stack1 : "")
    + "      </div>\r\n";
},"62":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "          "
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"medium") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1), depth0)) != null ? stack1 : "")
    + "\r\n";
},"64":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <div class=\"show-for-small-only "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"small") : depth0)) != null ? lookupProperty(stack1,"location") : stack1), depth0))
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"small") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1),{"name":"if","hash":{},"fn":container.program(65, data, 0),"inverse":container.program(59, data, 0),"data":data,"loc":{"start":{"line":114,"column":8},"end":{"line":118,"column":15}}})) != null ? stack1 : "")
    + "      </div>\r\n";
},"65":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "          "
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"small") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1), depth0)) != null ? stack1 : "")
    + "\r\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.program(16, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":18,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0, blockParams, depths),"inverse":container.program(28, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":19,"column":2},"end":{"line":65,"column":9}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"icon") : depth0),{"name":"if","hash":{},"fn":container.program(42, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":66,"column":2},"end":{"line":70,"column":9}}})) != null ? stack1 : "")
    + "  <div class=\"info-box\">\r\n    <div tabindex=\"-1\" class=\"ada-hidden ios-fix\">"
    + container.escapeExpression(alias2((depth0 != null ? lookupProperty(depth0,"slideAdaText") : depth0), depth0))
    + "</div>\r\n    <div tabindex=\"-1\" class=\"heading\">"
    + ((stack1 = alias2((depth0 != null ? lookupProperty(depth0,"heading") : depth0), depth0)) != null ? stack1 : "")
    + "</div>\r\n    <div tabindex=\"-1\" class=\"subheading\">"
    + ((stack1 = alias2((depth0 != null ? lookupProperty(depth0,"subheading") : depth0), depth0)) != null ? stack1 : "")
    + "</div>\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1),{"name":"if","hash":{},"fn":container.program(44, data, 0, blockParams, depths),"inverse":container.program(46, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":75,"column":4},"end":{"line":81,"column":11}}})) != null ? stack1 : "")
    + "  </div>\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"cta") : stack1),{"name":"if","hash":{},"fn":container.program(49, data, 0, blockParams, depths),"inverse":container.program(51, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":83,"column":0},"end":{"line":87,"column":7}}})) != null ? stack1 : "")
    + "\r\n<div class=\"shl-inline-styles\"></div>\r\n";
},"main_d":  function(fn, props, container, depth0, data, blockParams, depths) {

  var decorators = container.decorators, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(53, data, 0, blockParams, depths),"inverse":container.noop,"args":["logoPartial"],"data":data,"loc":{"start":{"line":89,"column":0},"end":{"line":122,"column":11}}}) || fn;
  return fn;
  }

,"useDecorators":true,"usePartial":true,"useData":true,"useDepths":true});
const renderTemplate = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template(data, options);
};

// Import templates & helpers
var _moduleConfig = {
  "name": "@sparta.homepage.modules/highlight-lowlight-legacy-module",
  "version": "1.0.3",
  "id": "highlightLowlightLegacyModule",
  "containerID": "homepageHighlightLowlightLegacyModule",
  "moduleName": "homepage-highlight-lowlight-legacy-module",
  "semantic": true,
  "superModule": false,
  "viewPort": ["small", "medium", "large"],
  "tag": "div",
  "nameSpace": "highlight-lowlight-legacy-module",
  "data": {
    "init": "highlightLowlightLegacyModuleJS"
  },
  "moduleParams": [{
    "name": "highlightContainers",
    "type": "object",
    "description": "Object containing default & signoff arrays identifying ad containers for highlights (AKA Super-highlights)."
  }, {
    "name": "lowlightContainers",
    "type": "object",
    "description": "Object containing default & signoff arrays identifying ad containers for lowlights (AKA (not super) highlights)."
  }],
  "layout": "layout",
  "render": function (context) {
    return renderTemplate$2(context, {
      "helpers": {
        json,
        link,
        layerContent,
        picture,
        curve,
        isArray,
        compare
      },
      "partials": {
        "_dynamicStyles": renderTemplate$1,
        "_highlight": renderTemplate
      }
    });
  },
  "template": renderTemplate$2,
  "partials": {
    "_dynamicStyles": renderTemplate$1,
    "_highlight": renderTemplate
  },
  "helpers": {
    json,
    link,
    layerContent,
    picture,
    curve,
    isArray,
    compare
  },
  "dependencies": {
    "@sparta.global.helpers/comparisons-helper": "^1.0.0",
    "@sparta.global.helpers/json-helper": "^1.0.0",
    "@sparta.global.helpers/picture-helper": "^1.0.0",
    "@sparta.global.helpers/sparta-helper": "^1.0.0",
    "@sparta.global.utilities/dom-utility": "^1.0.0",
    "@sparta.global.utilities/sparta-mediaquery-utility": "^1.0.0",
    "@sparta.global.utilities/sparta-module-utility": "^1.0.0",
    "@sparta.global.utilities/sparta-style-token-utility": "^1.1.9",
    "@sparta.global.utilities/sparta-style-utility": "^1.0.0",
    "@sparta.global.vendors/handlebars": "^4.7.7",
    "@sparta.global.vendors/lodash": "^4.17.21",
    "@sparta.global.vendors/picturefill": "^3.0.0",
    "@sparta.homepage.helpers/homepage-helper": "^1.0.0",
    "@sparta.homepage.utilities/animation-utility": "^1.0.5",
    "@sparta.homepage.utilities/modal-utility": "^1.0.0",
    "@sparta.homepage.utilities/remote-logging-utility": "^1.0.0",
    "@sparta.homepage.utilities/style-utility": "^1.0.0",
    "@sparta.sales.utilities/benchmark-utility": "^1.0.0"
  },
  "stores": []
};

/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
const containerTypes = ['highlight', 'super-highlight'];

/**
 * @export
 * @class highlightLowlightLegacyModuleJS
 * @extends {Module}
 *
 * @mockModuleParams {
 * "highlightContainers": {
 *     "default": [
 *       "BOA_HOME_SIGNON_SUPHIGHLIGHT_01",
 *       "BOA_HOME_SIGNON_SUPHIGHLIGHT_02"
 *     ]
 *   },
 *   "lowlightContainers": {
 *     "default": [
 *       "BOA_HOME_SIGNON_REGHIGHLIGHT_01",
 *       "BOA_HOME_SIGNON_REGHIGHLIGHT_02",
 *       "BOA_HOME_SIGNON_REGHIGHLIGHT_03",
 *       "BOA_HOME_SIGNON_REGHIGHLIGHT_04"
 *     ]
 *   }
 * }
 * @mockElement #highlight-lowlight-carousel
 * @mockElement #default-content {}
 */
class highlightLowlightLegacyModuleJS extends Module {
  /**
   * @description Creates an instance of highlightLowlightLegacyModuleJS
   * @method constructor
   * @param {Object} options options
   * @param {Object} context context
   * @returns {void}
   */
  constructor(options, context) {
    super({
      ...options,
      context,
      config: _moduleConfig
    });
    this.tiles = [...this.settings.target.querySelectorAll('.carousel-tile')];
    this.carouselEl = document.getElementById('highlight-lowlight-carousel');
    this.carouselSlider = this.carouselEl.parentElement;
    this.contentLoadedCount = 0;
    this.ctasBound = false;
    this.getClosestNumber = highlightLowlightLegacyModuleJS.getClosestNumber;
    this.getPageWidth = highlightLowlightLegacyModuleJS.getPageWidth;
    this.getWidth = highlightLowlightLegacyModuleJS.getWidth;
    this.impressionsFired = false;
    this.largeUpMq = window.matchMedia('(min-width: 1025px)');
    this.lastTileIdx = this.tiles.length - 1;
    this.scrollIntervals = [];
    this.scrolledByAnimation = false;
    this.smallMq = window.matchMedia('(max-width: 450px)');
    this.template = _moduleConfig.partials._highlight;
    this.highlightContainers = this.params.highlightContainers.default || [];
    this.lowlightContainers = this.params.lowlightContainers.default || [];
    this.highlightTiles = document.querySelectorAll('.highlight');
    this.lowlightTiles = document.querySelectorAll('.lowlight');
    this.allContainers = this.highlightContainers.concat(this.lowlightContainers);
    if (spaParams.siteParams.jsTimer) {
      benchmarkUtility.init(this);
    }
    this.Events.sub('HOMEPAGE_HIGHLIGHT_LOWLIGHT_MODULE_CONTENT_RENDERED', ({
      data
    }) => {
      this.injectStyles(data);
    });
  }

  /**
   * Initialize the module
   * @testExample init()
   * @testOutput undefined
   * @spy this.initAdContainer
   * @testExpect this.initAdContainer.callCount toEqual 6
   * @returns {void}
   */
  init() {
    this.defaultContent = JSON.parse(document.getElementById('default-content').dataset.jcr);
    this.allContainers.forEach(this.initAdContainer.bind(this));
    this.bindSliderEvents();
    Handlebars.registerHelper('isArray', function isArrayHelper(a, opts) {
      return Array.isArray(a) ? opts.fn(this) : opts.inverse(this);
    });
  }

  /**
   * @param {Object} content content jcr
   * @returns {void}
   */
  injectStyles(content = null) {
    const id = 'highlightLowlightStyles';
    const template = _moduleConfig.partials._dynamicStyles(content);
    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement('style');
      tag.id = id;
      tag.innerHTML = template;
      document.querySelector('head').append(tag);
    } else {
      tag.append(template);
    }
  }

  /**
   * Load ad content into frame.
   * @param {String} containerName - container to load ad content into
   * @testExample initAdContainer('BOA_HOME_SIGNON_SUPHIGHLIGHT_01')
   * @testOutput undefined
   * @testExpect this.Events.sub.called toEqual true
   * @spy this.Events.sub
   * @returns {void}
   */
  initAdContainer(containerName) {
    this.Events.sub(`target-${containerName}`, this.loadContent.bind(this), true);
  }

  /**
   * Load content into (super)highlight
   * NOTE: Full Unit tests awaiting Sparta event implementation in test runner
   * @testExample loadContent({
   *  event: "target-BOA_HOME_SIGNON_SUPHIGHLIGHT_01",
   *  data: {
   *    META: { CONTAINER_TYPE: "super-highlight", TEMPLATE: ""}
   *  }
   * })
   * @testExpect performance.mark.called toEqual true
   * @testExpect performance.measure.called toEqual true
   * @testExpect console.log.called toEqual true
   * @mockSpaParams { siteParams: { jsTimer: true } }
   * @spy performance.mark
   * @spy performance.measure
   * @spy console.log
   * @param {Event} event Event fired by targeting module or simulated fallback from timeout
   * @returns {void}
   */
  loadContent(event = {
    event: '',
    data: ''
  }) {
    const container = event.event.replace('target-', '');
    const highlightPos = this.allContainers.indexOf(container);
    if (spaParams.siteParams.jsTimer && performance.mark !== undefined) {
      try {
        performance.mark(`${container}-loadContent-end`);
        performance.measure(`${container}-loadContent`, `${container}-loadContent-start`, `${container}-loadContent-end`);
        const measures = performance.getEntriesByName(`${container}-loadContent`);
        const measure = measures[0];
        console.log(`${container} started loading content after ${measure.duration}ms`);
      } catch (e) {
        console.log(`${container} started loading content at ${performance.now()}ms`);
      }
    }
    if (highlightPos >= 0) {
      let content;
      let defaultContent = false;

      // check returned META informaton
      if (event.data && event.data.META && event.data.META.CONTAINER_TYPE) {
        const metaContainer = event.data.META.CONTAINER_TYPE.toLowerCase();
        if (containerTypes.includes(metaContainer) && highlightLowlightLegacyModuleJS.isValidJson(event.data)) {
          content = event.data;
        } else {
          content = this.defaultContent[highlightPos];
          defaultContent = true;
          remoteConsole.error(`Targeting Error: Wrong container type: ${metaContainer}. ` + `One of "${containerTypes.join(',')}"  expected.`);
        }
      } else {
        content = this.defaultContent[highlightPos];
        defaultContent = true;
        remoteConsole.error('Targeting Error: missing Meta information');
      }
      const windowWidth = window.innerWidth;
      if (windowWidth < 1025) {
        content.slideAdaText = `Slide ${highlightPos + 1} of 6`;
      }
      const div = this.carouselEl.querySelector(`div.carousel-tile:nth-child(${highlightPos + 1})`);
      if (!div) {
        return;
      }
      highlightLowlightLegacyModuleJS.renderContent(div, this.template(content));
      modalUtility.init(content);
      if (this.Events) {
        this.Events.pub('HOMEPAGE_HIGHLIGHT_LOWLIGHT_MODULE_CONTENT_RENDERED', content);
      }
      if (defaultContent) {
        div.setAttribute('data-default-content', container);
      } else {
        div.setAttribute('data-ad-container', container);
        if (content.clickToken) {
          div.setAttribute('data-ad-click-token', content.clickToken);
        }
      }

      // Only super highlights can have video
      if (content.videoBackground && highlightPos < 2) {
        // New instances of highlight video backgrounds.
        const videoBackground = new VideoBackground(this, div, content.videoBackground);
        videoBackground.init();
      }
      if (content.animations) {
        animationUtility.init(div, content.animations);
      }
      this.contentLoadedCount++;

      // if IE manually call picturefill method
      if (navigator.appName === 'Microsoft Internet Explorer' || !!(navigator.userAgent.match(/Trident/) || navigator.userAgent.match(/rv:11/))) {
        setTimeout(() => picturefill(), 1500);
      }
      this.initCarouselIfLoaded();
    }
  }

  /**
   * Initialize carousel once all containers have loaded
   * NOTE: Cannot test until framework supports file level mocking
   * @returns {void}
   */
  initCarouselIfLoaded() {
    if (this.contentLoadedCount === this.allContainers.length) {
      this.initCarousel();
    } else if (this.contentLoadedCount > this.allContainers.length) {
      this.handleSizeChange();
    }
  }

  /**
   * Builds highlight slider if medium or smaller view
   * @testExample handleSizeChange()
   * @testOutput undefined
   * @spy this.destructSliderContainer
   * @testExpect this.destructSliderContainer.callCount toEqual 1
   * @returns {void}
   */
  handleSizeChange() {
    if (!this.largeUpMq.matches) {
      this.buildSliderContainer();
    } else {
      this.destructSliderContainer();
    }
  }

  /**
   * Carousel initializer
   * @testExample initCarousel()
   * @testOutput undefined
   * @spy this.fireImpressions
   * @mockDelay 250
   * @testExpect this.fireImpressions.callCount toEqual 1
   * @testExpect this.settings.target.classList.contains('loaded') toEqual true
   * @returns {void}
   */
  initCarousel() {
    // trigger size change listener
    this.handleSizeChange();

    // Give slider a chance to initialize first
    _.delay(this.fireImpressions.bind(this), 250);
    this.settings.target.classList.add('loaded');
    this.evenSlideHeight();
    setTimeout(() => {
      this.settings.target.classList.add('loaded');
    }, 1000);
  }

  /**
   * Fire cm impressions
   * @testExample fireImpressions()
   * @testOutput undefined
   * @mockVariable window fireImpressions () => {}
   * @testExpect this.impressionsFired toEqual true
   * @returns {void}
   */
  fireImpressions() {
    if (typeof window.fireImpressions === 'function' && !this.impressionsFired) {
      window.fireImpressions();
      this.impressionsFired = true;
    }
  }

  /**
   * Styles the highlight container to create a slider.
   * @returns {void}
   */
  buildSliderContainer() {
    this.calcSliderSizes(totalSize => {
      if (totalSize) {
        this.carouselEl.style.width = `${totalSize}px`;
        this.maxScrollLeft = this.carouselSlider.scrollWidth - this.getPageWidth();
      }
    });
    this.carouselSlider.classList.add('sliderized');
  }

  /**
   * Removes style and 'sliderized' class from highlights container
   * and re-applies cached styling of highlight-tiles
   * to go back to large-up default appearance.
   * @returns {void}
   */
  destructSliderContainer() {
    this.carouselEl.removeAttribute('style');
    this.carouselSlider.removeAttribute('style');
    this.carouselSlider.classList.remove('sliderized');
    this.tiles.forEach(tile => {
      tile.removeAttribute('style');
      tile.removeAttribute('tabindex');
      tile.classList.remove('inactive-tile');
      tile.classList.remove('active-tile');
      if (tile.querySelector('a')) {
        tile.querySelector('a').removeAttribute('tabindex');
      }
    });
  }

  /**
   * Calculates the width of all the tiles in the carousel.
   * Uses setTimeout to wait for highlight content to load
   * before calculating widths.
   * @param {Function} totalWidthCb - Callback function to handle the total calculated width.
   * @returns {void}
   */
  calcSliderSizes(totalWidthCb) {
    let totalWidth = 0;
    setTimeout(() => {
      this.tiles.forEach((tile, idx) => {
        const tileHeight = tile.style.height;
        tile.removeAttribute('style');
        tile.style.height = tileHeight;
        totalWidth += this.getWidth(tile);
        if (idx === this.lastTileIdx) {
          if (this.getNumberOfTilesToDisplay() >= 2) {
            // Add half a tile of spacing to the end of the slider.
            totalWidth += this.getWidth(tile) / 2;
          } else {
            totalWidth += 15; // Add 15px of padding for single slide resolutions.
          }
        }
      });
      totalWidthCb(totalWidth);
    }, 0);
  }

  /**
   * Binds slider related events.
   * @returns {void}
   */
  bindSliderEvents() {
    this.largeUpMq.addListener(this.handleSizeChange.bind(this));
    ['orientationchange', 'resize'].forEach(eventType => {
      if (this.sizeChangeTimeoutId) {
        clearTimeout(this.sizeChangeTimeoutId);
      }
      this.sizeChangeTimeoutId = setTimeout(() => {
        window.addEventListener(eventType, this.evenSlideHeight.bind(this));
        window.addEventListener(eventType, this.handleSizeChange.bind(this));
      }, 100);
    });
    this.bindScrollListener();
    this.bindFocusListener();
  }

  /**
   * Even slide height
   * Added this since the carousel doesn't provide a flex and heights are not even.
   * The method is debounced on window resize.
   * @returns {void}
   */
  evenSlideHeight() {
    const heights = [];
    this.tiles.forEach(slide => {
      const link = slide.querySelector('.link');

      // remove height from style to default auto
      Object.assign(slide.style, {
        height: ''
      });

      // force static position to get correct heights due to absolute
      if (link && !this.largeUpMq.matches) {
        Object.assign(link.style, {
          position: 'static'
        });
      }
      heights.push(slide.clientHeight);

      // remove position and use css absolute value
      if (link) {
        Object.assign(link.style, {
          position: ''
        });
      }
    });
    if (heights.length && !this.largeUpMq.matches) {
      const maxHeight = Math.max.apply(null, heights);
      this.tiles.forEach((slide, i) => {
        if (i >= 0) {
          window.requestAnimationFrame(() => {
            Object.assign(slide.style, {
              height: `${maxHeight}px`
            });
          });
        }
      });

      // Update the parent so it doesn't cause the tiles to stack while resizing window.
      Object.assign(this.carouselSlider.style, {
        height: `${maxHeight + 15}px`
      });
    }
  }

  /**
   * Bind listener to the slider scroll event to calculate amount scrolled
   * @returns {void}
   */
  bindScrollListener() {
    this.scrolling = false;
    this.endScrollPosX = 0;
    this.carouselSlider.addEventListener('scroll', this.handleScrolling.bind(this));
  }

  /**
   * Bind listener to the focus event on a carousel-tile link
   * @returns {void}
   */
  bindFocusListener() {
    DomUtility.delegateEvent('focusin', '.carousel-tile', this.carouselSlider, e => {
      const tile = e.target.parentElement;
      tile.style.outline = '1px dotted';
      tile.style.outlineOffset = '1px';
    }, {
      once: false,
      useCapture: true
    });
    DomUtility.delegateEvent('focusout', '.carousel-tile', this.carouselSlider, e => {
      const tile = e.target.parentElement;
      tile.style.removeProperty('outline');
      tile.style.removeProperty('outline-offset');
    }, {
      once: false,
      useCapture: true
    });
  }

  /**
   * handleScrolling
   * @returns {void}
   */
  handleScrolling() {
    if (!this.scrolling) {
      window.requestAnimationFrame(() => {
        this.scrolling = false;
        if (this.carouselSlider.scrollLeft === this.maxScrollLeft) {
          this.tiles[this.lastTileIdx].querySelector('.clickable-highlight').focus({
            preventScroll: true
          });
        } else {
          const closestTileFromLeft = this.getClosestTileFromLeft();
          closestTileFromLeft.querySelector('.clickable-highlight').focus({
            preventScroll: true
          });
        }
      });
      this.scrolling = true;
    }
  }

  /**
   * getClosestTileFromLeft
   * @description Returns the closest tile from the left part of the parent scroll.
   * @returns {Element} closest tile from left
   */
  getClosestTileFromLeft() {
    const keyedTiles = {};
    const {
      scrollLeft
    } = this.carouselSlider;
    this.tiles.forEach(tile => {
      keyedTiles[tile.offsetLeft] = tile;
    });
    const closestNumber = this.getClosestNumber(scrollLeft, Object.keys(keyedTiles));
    return keyedTiles[closestNumber];
  }

  /**
   * getNumberOfTilesToDisplay
   * @throws {Error} Unable to getNumberOfTilesToDisplay. No tiles exists.
   * @returns {Number} n tiles that can be displayed
   */
  getNumberOfTilesToDisplay() {
    if (!this.tiles.length) {
      throw new Error('Unable to getNumberOfTilesToDisplay. No tiles exist.');
    }
    const carouselSliderWidth = this.carouselSlider.getBoundingClientRect().width;
    const firstSlideClientWidth = this.tiles[0].getBoundingClientRect().width + 30;
    return Math.floor(carouselSliderWidth / firstSlideClientWidth);
  }

  /**
   * Gets an elements width with margin and border
   * @static
   * @param {object} el - element to calculate for
   * @returns {Number} width with margin and border
   */
  static getWidth(el) {
    const style = window.getComputedStyle(el);
    const width = parseFloat(style.width);
    const margin = parseFloat(style.marginLeft) + parseFloat(style.marginRight);
    const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const border = parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
    return width + margin + padding + border;
  }

  /**
   * Funcion for getting width of the page
   * @static
   * @returns {Number} width of the page
   */
  static getPageWidth() {
    return Math.max(document.body.scrollWidth, document.documentElement.scrollWidth, document.body.offsetWidth, document.documentElement.offsetWidth, document.documentElement.clientWidth);
  }

  /**
   * getClosestNumber
   * @description Returns the closest number to the starting number
   * @static
   * @param {Number} startingNum Starting number
   * @param {Array} numbers Numbers to check against starting number
   * @returns {Number} closest number from the array
   */
  static getClosestNumber(startingNum, numbers) {
    let curr = numbers[0];
    let diff = Math.abs(startingNum - curr);
    numbers.forEach(n => {
      const newDiff = Math.abs(startingNum - n);
      if (newDiff < diff) {
        diff = newDiff;
        curr = n;
      }
    });
    return curr;
  }

  /**
   * Validates JSON object for XSS
   * @param {object} jsonObj data for template
   * @returns {boolean} isValidJson
   */
  static isValidJson(jsonObj) {
    try {
      const content = JSON.stringify(jsonObj);
      const scriptExp = /(?:<[^>]+\s)(on\S+)(\s+)?=(\s+)?["']?/ig;
      const scriptTag = /<[^>]*script|(?:<[^>]+\s)(on\S+)(\s+)?=(\s+)?["']?/ig;
      const iFrameTag = /<[^>]*iframe/ig;
      if (content.match(scriptExp) || content.match(scriptTag) || content.match(iFrameTag)) {
        return false;
      }
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  /**
   * Renders content in element
   * @param {HTMLElement} elem elem
   * @param {string} content context
   * @returns {void}
   */
  static renderContent(elem, content) {
    elem.innerHTML = content;
  }
}
registerModuleClass({
  moduleClass: highlightLowlightLegacyModuleJS,
  moduleConfig: _moduleConfig
});
const moduleConfig = _moduleConfig;

export { highlightLowlightLegacyModuleJS as default, moduleConfig };
